import { describeOverShort } from '@didto/domain';
import { Badge, EmptyState, Loading, PageHeader, Pills } from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOwnerReview } from '@/app/ownerReview';
import {
  CLOSE_KIND_LABEL,
  addDays,
  fetchShiftList,
  reviewKeys,
  useReviewCounts,
} from '@/lib/review';
import { overShortTone } from '@/routes/pos/shiftSummary';
import { Figure, ReadError, ReviewStatusBadge, useFormats } from './reviewItems';

/** The owner's shifts: the last days at a glance, what is still to check, and the way into each shift. */
export function ShiftsPage() {
  const { locationId } = useOwnerReview();
  const nav = useNavigate();
  const f = useFormats();
  const counts = useReviewCounts(locationId);
  const today = counts.data?.today ?? new Date().toISOString().slice(0, 10);
  const [range, setRange] = useState<'7' | '14' | '31'>('7');
  const [only, setOnly] = useState<'all' | 'review'>('all');
  // the last day listed: today unless the owner went back (the counts look back 90 days; older shifts are found here)
  const [end, setEnd] = useState<string | null>(null);
  const to = end ?? today;
  const from = addDays(to, -(Number(range) - 1));
  const later = addDays(to, Number(range));
  const shifts = useQuery({
    queryKey: reviewKeys.shifts(locationId, from, to, only === 'review'),
    queryFn: () => fetchShiftList(locationId, from, to, only === 'review'),
    refetchInterval: 60_000,
  });
  const c = counts.data;
  return (
    <div className="d-page" data-testid="shifts-page">
      <PageHeader
        eyebrow="Owner"
        title="Shifts"
        subtitle="Every drawer shift with its figures, as they were frozen at the close and as they are now."
        actions={
          <>
            <Link to="/shifts/review" className="d-btn d-btn--sm" data-testid="to-queue">
              To check{c ? ` · ${c.red_open}` : ''}
            </Link>
            <Link
              to={`/shifts/day/${c?.oldest_unreviewed_date ?? today}`}
              className="d-btn d-btn--sm d-btn--primary"
              data-testid="to-day"
            >
              Day view
            </Link>
          </>
        }
      />
      <ReadError error={counts.error} hasData={!!c} onRetry={() => void counts.refetch()} />
      {c ? (
        <div className="d-stats" data-testid="review-counts">
          <Figure
            label="To check"
            value={c.red_open}
            tone={c.red_open > 0 ? 'danger' : undefined}
            testId="count-red"
            hint={`${c.follow_ups} follow-up${c.follow_ups === 1 ? '' : 's'}`}
          />
          <Figure
            label="Shifts not reviewed"
            value={c.unreviewed_shifts}
            tone={c.unreviewed_shifts > 0 ? 'warning' : undefined}
            testId="count-unreviewed"
            hint={`${
              c.oldest_unreviewed_date
                ? `oldest ${f.date(c.oldest_unreviewed_date)}`
                : 'last 31 days'
            }${c.older_unreviewed_shifts > 0 ? ` · ${c.older_unreviewed_shifts} older, to 90 days` : ''}`}
          />
          <Figure
            label="Non-cash rows to check"
            value={c.unmarked_rows}
            tone={c.unmarked_rows > 0 ? 'warning' : undefined}
            testId="count-unmarked"
            hint={<span className="d-mono">{f.money(c.unmarked_minor)}</span>}
          />
        </div>
      ) : counts.isLoading ? (
        <Loading label="Loading what is to check" rows={1} />
      ) : null}
      <div className="d-spread">
        <Pills
          ariaLabel="Days"
          value={range}
          onChange={setRange}
          options={[
            { value: '7', label: '7 days' },
            { value: '14', label: '14 days' },
            { value: '31', label: '31 days' },
          ]}
        />
        <Pills
          ariaLabel="Which shifts"
          value={only}
          onChange={setOnly}
          options={[
            { value: 'all', label: 'All' },
            { value: 'review', label: 'To check' },
          ]}
        />
      </div>
      <div className="d-spread" data-testid="shifts-period">
        <button
          type="button"
          className="d-btn d-btn--sm"
          onClick={() => setEnd(addDays(from, -1))}
          data-testid="shifts-earlier"
        >
          Earlier
        </button>
        <span className="d-muted" data-testid="shifts-period-label">
          {end ? `${f.date(from)} to ${f.date(to)}` : `The last ${range} days`}
        </span>
        <button
          type="button"
          className="d-btn d-btn--sm"
          onClick={() => setEnd(later >= today ? null : later)}
          disabled={!end}
          data-testid="shifts-later"
        >
          Later
        </button>
      </div>
      <ReadError
        error={shifts.error}
        hasData={!!shifts.data}
        onRetry={() => void shifts.refetch()}
        testId="shifts-error"
      />
      {shifts.isLoading ? (
        <Loading label="Loading the shifts" rows={4} />
      ) : (
        <div className="d-list" data-testid="shift-list">
          {(shifts.data?.shifts ?? []).map((sh) => (
            <button
              key={sh.id}
              type="button"
              className="d-row d-row--button"
              onClick={() => nav(`/shifts/${sh.id}`)}
              data-testid="shift-row"
              data-status={sh.review.status}
            >
              <div className="d-row__main">
                <div className="d-row__title">
                  #{sh.seq} · {sh.drawer?.name ?? 'Drawer'} · {sh.cashier.name}
                </div>
                <div className="d-row__meta">
                  {f.date(sh.business_date)} · {f.at(sh.opened_at)}
                  {sh.closed_at ? ` → ${f.at(sh.closed_at)}` : ' · open'}
                  {sh.close_kind ? ` · ${CLOSE_KIND_LABEL[sh.close_kind] ?? sh.close_kind}` : ''}
                  {sh.adjusted ? ' · moved since the close' : ''}
                  {sh.unmarked_rows > 0 ? ` · ${sh.unmarked_rows} non-cash to check` : ''}
                </div>
              </div>
              <div className="rv-side">
                {sh.over_short_minor != null && (
                  <Badge
                    tone={overShortTone(
                      sh.adjusted_figures.over_short_minor ?? sh.over_short_minor,
                      sh.tolerance_minor,
                    )}
                  >
                    {describeOverShort(
                      sh.adjusted_figures.over_short_minor ?? sh.over_short_minor,
                      f.money,
                    )}
                    {sh.adjusted_figures.corrected ? ' · corrected' : ''}
                  </Badge>
                )}
                <ReviewStatusBadge status={sh.status === 'closed' ? sh.review.status : 'open'} />
              </div>
            </button>
          ))}
          {shifts.data?.shifts.length === 0 && <EmptyState title="No shifts in these days." />}
        </div>
      )}
    </div>
  );
}
